/**
 * Responsive wrapper for creating simple modal using material.ui dialog
 */
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, ModalProps } from '@mui/material';
import { DialogProps } from '@mui/material/Dialog/Dialog';
import classNames from 'classnames';
import React, { Fragment, ReactElement, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IosClose from 'react-ionicons/lib/IosClose';

type Props = {
  onClose: ModalProps['onClose'];
  title?: string;
  titleIcon?: string | ReactNode;
  alignTitleStart?: boolean;
  translateTitle?: boolean;
  closeIconVisible?: boolean;
  closeButtonVisible?: boolean;
  closeButtonText?: string;
  actions?: ReactNode[];
  className?: string;
  contentClassName?: string;
  outsideContentChildren?: ReactNode;
  fullHeight?: boolean;
  titleUnderline?: boolean;
  actionsTopBorderline?: boolean;
  disableBackdropDismiss?: boolean;
};

const Modal = (props: Props & DialogProps) => {
  const {
    open,
    onClose,
    children,
    title,
    titleIcon,
    alignTitleStart,
    translateTitle,
    closeIconVisible,
    closeButtonVisible,
    actions,
    className,
    contentClassName,
    outsideContentChildren,
    closeButtonText,
    titleUnderline,
    actionsTopBorderline,
    disableBackdropDismiss,
  } = props;
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(open);

  const translateTitleOption = translateTitle === undefined ? true : translateTitle;
  const closeIconVisibleOption = closeIconVisible === undefined ? true : closeIconVisible;
  const closeButtonVisibleOption = closeButtonVisible === undefined ? false : closeButtonVisible;

  // Remove custom props from dialog props
  const dialogProps = { ...props };

  [
    'translateTitle',
    'closeIconVisible',
    'closeButtonVisible',
    'actions',
    'className',
    'contentClassName',
    'outsideContentChildren',
    'closeButtonText',
    'fullHeight',
    'spacey',
    'titleUnderline',
    'actionsTopBorderline',
    'titleIcon',
    'alignTitleStart',
    'title',
    'disableBackdropDismiss',
  ].forEach((prop) => delete dialogProps[prop]);

  // Detect open state change and update isOpen
  useEffect(() => {
    setOpen(open);
  }, [open]);

  return (
    <Dialog
      fullScreen={false}
      aria-labelledby="global-modal"
      open={isOpen}
      className={classNames('global-modal', className, {
        'full-width': dialogProps.fullWidth,
        'full-height': props.fullHeight,
        'max-width': props.maxWidth,
      })}
      {...dialogProps}
      onClose={(e: object, reason: 'backdropClick' | 'escapeKeyDown') => {
        // Disable backdrop click.
        if (disableBackdropDismiss && reason === 'backdropClick') {
          return;
        }

        onClose?.(e, reason);
      }}
    >
      {/* title */}
      {title || closeIconVisibleOption ? (
        <DialogTitle
          id="global-modal-title"
          variant="h2"
          component="h2"
          className={classNames('global-modal-title', {
            underline: titleUnderline !== undefined ? titleUnderline : !!title,
            'global-modal-title__start': alignTitleStart,
          })}
        >
          {title ? (
            <>
              {titleIcon &&
                (typeof titleIcon === 'string' ? (
                  <i className={classNames('icon', titleIcon, 'title-icon')} />
                ) : (
                  React.cloneElement(titleIcon as ReactElement, {
                    className: classNames('icon', (titleIcon as ReactElement)?.props?.className, 'title-icon'),
                  })
                ))}
              <span
                className="title"
                style={{
                  paddingRight: closeIconVisibleOption ? '28px' : 'auto',
                }}
              >
                {translateTitleOption ? <div dangerouslySetInnerHTML={{ __html: t(title) }} /> : title}
              </span>
            </>
          ) : null}

          {/* close icon */}
          {closeIconVisibleOption ? (
            <IconButton onClick={(e) => onClose(e, null)} className="close-icon" edge="end">
              <IosClose />
            </IconButton>
          ) : null}
        </DialogTitle>
      ) : null}

      {/* content */}
      <DialogContent className={classNames('modal-content', contentClassName)}>{children}</DialogContent>

      {outsideContentChildren}

      {/* actions */}
      {actions?.length || closeButtonVisibleOption ? (
        <DialogActions
          className={classNames('modal-actions', {
            overline: actionsTopBorderline !== undefined ? actionsTopBorderline : false,
          })}
        >
          <div className={classNames('actions', { 'mt-2': actionsTopBorderline })}>
            {actions?.map((action, actionIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={'global-modal-action-' + actionIndex}>{action}</Fragment>
            ))}
          </div>

          {closeButtonVisibleOption ? (
            <Button
              onClick={() => onClose(null, null)}
              color="primary"
              className={classNames('close-action-btn', { 'mt-2': actionsTopBorderline })}
            >
              {t(closeButtonText || 'common.close')}
            </Button>
          ) : null}
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default Modal;
